import * as THREE from "three";
import { DROPS, type DropState, type ItemId } from "@dyson/shared";
import { drawItemIcon } from "./TileAtlas";

/** How big a loose thing is drawn, in blocks. */
const SIZE = 1.3;
/** Pixels across the little canvas each item's art is drawn onto. */
const ART = 48;
/** How far a pile's extra copies sit from the first, in blocks. */
const HEAP = 0.3;
/** How quickly the drawn place catches up with the one the server sent, per second. */
const CATCH_UP = 14;
/** How far ahead of the last snapshot a moving pile is drawn (s), so a throw doesn't trail behind itself. */
const LEAD = 0.05;

interface Pile {
  group: THREE.Group;
  sprites: THREE.Sprite[];
  item: ItemId;
  /** Where it's drawn (in its own frame), easing toward where the server says it is. */
  at: { x: number; y: number };
  /** Radians a second it turns, either way: so no two piles tumble alike. */
  spin: number;
}

/** How many copies of the thing a pile of `n` is drawn as, Minecraft-style: a bigger heap for more of it. */
const copies = (n: number): number => (n >= 32 ? 4 : n >= 16 ? 3 : n >= 5 ? 2 : 1);

/**
 * Loose items lying about (see DROPS): piles of things thrown or dropped,
 * hanging in the air where they were let go of and turning slowly, for
 * anyone to float into and pick up. The simulation is the server's; this
 * draws what the snapshots say, easing each pile toward where it's told so
 * that 20 snapshots a second read as smooth drift.
 */
export class LooseItems {
  readonly group = new THREE.Group();
  private readonly textures = new Map<ItemId, THREE.Texture>();
  private readonly piles = new Map<string, Pile>();

  constructor() {
    this.group.name = "drops";
  }

  /**
   * Draw every pile in `drops`; `place` turns a pile's own coordinates into
   * the world (through its ship, if it's aboard one). Piles no longer sent
   * are dropped.
   */
  update(dt: number, drops: Iterable<DropState>, place: (d: DropState, x: number, y: number, out: THREE.Vector3) => void): void {
    const seen = new Set<string>();
    for (const d of drops) {
      seen.add(d.id);
      let pile = this.piles.get(d.id);
      if (!pile || pile.item !== d.item) {
        if (pile) this.forget(d.id);
        pile = this.make(d);
      }
      // As many copies as the heap is deep, which changes as piles run together.
      const want = copies(d.n);
      while (pile.sprites.length < want) this.addSprite(pile, pile.sprites.length);
      while (pile.sprites.length > want) {
        const s = pile.sprites.pop()!;
        pile.group.remove(s);
        s.material.dispose();
      }
      // Toward where the server says it is, a little ahead of it while it's moving.
      const k = Math.min(1, CATCH_UP * dt);
      pile.at.x += (d.position.x + d.velocity.x * LEAD - pile.at.x) * k;
      pile.at.y += (d.position.y + d.velocity.y * LEAD - pile.at.y) * k;
      place(d, pile.at.x, pile.at.y, pile.group.position);
      for (const s of pile.sprites) s.material.rotation += pile.spin * dt;
    }
    for (const id of [...this.piles.keys()]) if (!seen.has(id)) this.forget(id);
  }

  dispose(): void {
    for (const id of [...this.piles.keys()]) this.forget(id);
    for (const t of this.textures.values()) t.dispose();
    this.textures.clear();
  }

  private make(d: DropState): Pile {
    const group = new THREE.Group();
    group.position.z = 0.6;
    this.group.add(group);
    // A tumble of its own, either way, so two piles of the same thing don't turn in step.
    const h = Math.abs(hash(d.id));
    const pile: Pile = { group, sprites: [], item: d.item, at: { ...d.position }, spin: (h % 2 ? -1 : 1) * DROPS.spin * (0.5 + ((h >> 1) % 100) / 100) };
    this.piles.set(d.id, pile);
    this.addSprite(pile, 0);
    return pile;
  }

  private addSprite(pile: Pile, i: number): void {
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.textureFor(pile.item), transparent: true, fog: false }));
    sprite.scale.set(SIZE, SIZE, 1);
    // The heap: the first in the middle, the rest round it, each turned differently.
    const a = (i * Math.PI * 2) / 3 + 0.4;
    sprite.position.set(i === 0 ? 0 : Math.cos(a) * HEAP, i === 0 ? 0 : Math.sin(a) * HEAP, i * 0.01);
    sprite.material.rotation = i * 0.7;
    pile.group.add(sprite);
    pile.sprites.push(sprite);
  }

  private forget(id: string): void {
    const pile = this.piles.get(id);
    if (!pile) return;
    for (const s of pile.sprites) s.material.dispose();
    this.group.remove(pile.group);
    this.piles.delete(id);
  }

  /** The item's inventory art as a texture, drawn once per kind of thing and kept. */
  private textureFor(item: ItemId): THREE.Texture {
    let tex = this.textures.get(item);
    if (tex) return tex;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = ART;
    drawItemIcon(item, canvas);
    tex = new THREE.CanvasTexture(canvas);
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    this.textures.set(item, tex);
    return tex;
  }
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h | 0;
}
